// src/pages/finance-portal/general-ledger/GeneralLedger.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, Skeleton, Tabs, Tab, Autocomplete,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function GeneralLedger() {
    const [tabValue, setTabValue] = useState(0);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // GL detail
    const [glEntries, setGlEntries] = useState([]);
    const [glLoading, setGlLoading] = useState(false);
    const [error, setError] = useState(null);

    // Income Statement
    const [incomeStatement, setIncomeStatement] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Balance Sheet
    const [balanceSheet, setBalanceSheet] = useState(null);
    const [bsLoading, setBsLoading] = useState(false);

    // Fetch accounts for autocomplete
    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) setAccounts(res.data.data || []);
            } catch (err) { /* silent */ }
        })();
    }, []);

    const getAccountLabel = (acct) => acct ? `${acct.account_number} - ${acct.name}` : '';

    const fetchGLDetail = useCallback(async () => {
        setGlLoading(true);
        setError(null);
        try {
            let res;
            const params = {};
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            if (selectedAccount) {
                res = await axiosInstance.get(`/api/finance/reports/account-ledger/${selectedAccount.id}`, { params });
            } else {
                res = await axiosInstance.get('/api/finance/reports/general-ledger', { params });
            }
            if (res.data.success) {
                setGlEntries(res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load general ledger');
        } finally {
            setGlLoading(false);
        }
    }, [selectedAccount, fromDate, toDate]);

    const fetchIncomeStatement = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            const res = await axiosInstance.get('/api/finance/reports/income-statement', { params });
            if (res.data.success) {
                setIncomeStatement(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load income statement');
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate]);

    const fetchBalanceSheet = useCallback(async () => {
        setBsLoading(true);
        setError(null);
        try {
            const params = {};
            if (toDate) params.as_of_date = toDate;
            const res = await axiosInstance.get('/api/finance/reports/balance-sheet', { params });
            if (res.data.success) {
                setBalanceSheet(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load balance sheet');
        } finally {
            setBsLoading(false);
        }
    }, [toDate]);

    useEffect(() => {
        if (tabValue === 0) fetchGLDetail();
        else if (tabValue === 1) fetchIncomeStatement();
        else if (tabValue === 2) fetchBalanceSheet();
    }, [tabValue, fetchGLDetail, fetchIncomeStatement, fetchBalanceSheet]);

    // Calculate running balance for GL entries
    const entriesWithBalance = glEntries.reduce((acc, entry) => {
        const prevBalance = acc.length > 0 ? acc[acc.length - 1]._running_balance : 0;
        const debit = parseFloat(entry.debit) || 0;
        const credit = parseFloat(entry.credit) || 0;
        acc.push({ ...entry, _running_balance: prevBalance + debit - credit });
        return acc;
    }, []);

    const renderReportSection = (title, items, isBold = false) => {
        if (!items || items.length === 0) return null;
        return (
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1, pl: 1 }}>{title}</Typography>
                {items.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 0.75,
                        bgcolor: idx % 2 === 0 ? '#fff' : '#f8fafc', borderRadius: '6px' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', fontWeight: isBold ? 700 : 400, pl: item.indent ? item.indent * 2 : 0 }}>
                            {item.account_number ? `${item.account_number} - ` : ''}{item.name || item.account_name}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: isBold ? 700 : 600 }}>
                            {formatCurrency(item.balance || item.amount)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MenuBookIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>General Ledger</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>GL detail, income statement & balance sheet</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Filters */}
                <Paper sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        {tabValue === 0 && (
                            <Autocomplete
                                options={accounts}
                                value={selectedAccount}
                                onChange={(_, val) => setSelectedAccount(val)}
                                getOptionLabel={getAccountLabel}
                                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                size="small"
                                sx={{ minWidth: 280 }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="All Accounts"
                                        InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' } }}
                                        InputLabelProps={{ sx: { fontFamily } }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem' }}>{getAccountLabel(option)}</Typography>
                                    </li>
                                )}
                            />
                        )}
                        <TextField label="From Date" type="date" value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                            size="small" sx={{ width: 170 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                        <TextField label="To Date" type="date" value={toDate}
                            onChange={e => setToDate(e.target.value)}
                            size="small" sx={{ width: 170 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                    </Box>
                </Paper>

                {/* Tabs */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="GL Report" />
                    <Tab label="Income Statement" />
                    <Tab label="Balance Sheet" />
                </Tabs>

                {/* GL Report Tab */}
                {tabValue === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Date', 'Entry #', 'Description', 'Account', 'Debit', 'Credit', 'Balance'].map(h => (
                                            <TableCell key={h} sx={{
                                                fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                                                textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                                                textAlign: ['Debit', 'Credit', 'Balance'].includes(h) ? 'right' : 'left',
                                            }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {glLoading ? (
                                        [1, 2, 3, 4, 5].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : entriesWithBalance.length === 0 ? (
                                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <MenuBookIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No ledger entries found</Typography>
                                        </TableCell></TableRow>
                                    ) : entriesWithBalance.map((entry, idx) => (
                                        <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(entry.date || entry.entry_date)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>{entry.entry_number || entry.journal_entry_id || '-'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {entry.description || entry.memo || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {entry.account_number ? `${entry.account_number} - ` : ''}{entry.account_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: parseFloat(entry.debit) > 0 ? '#1e293b' : '#cbd5e1', textAlign: 'right', fontWeight: parseFloat(entry.debit) > 0 ? 600 : 400 }}>
                                                {parseFloat(entry.debit) > 0 ? formatCurrency(entry.debit) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: parseFloat(entry.credit) > 0 ? '#1e293b' : '#cbd5e1', textAlign: 'right', fontWeight: parseFloat(entry.credit) > 0 ? 600 : 400 }}>
                                                {parseFloat(entry.credit) > 0 ? formatCurrency(entry.credit) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: entry._running_balance >= 0 ? '#166534' : '#991b1b', textAlign: 'right' }}>
                                                {formatCurrency(Math.abs(entry._running_balance))}
                                                {entry._running_balance < 0 ? ' CR' : ' DR'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}

                {/* Income Statement Tab */}
                {tabValue === 1 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
                        ) : !incomeStatement ? (
                            <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 4 }}>No data available</Typography>
                        ) : (
                            <>
                                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>Income Statement</Typography>
                                {renderReportSection('Revenue', incomeStatement.revenue || incomeStatement.income)}
                                {renderReportSection('Cost of Goods Sold', incomeStatement.cogs || incomeStatement.cost_of_goods_sold)}
                                {incomeStatement.gross_profit !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#f0fdf4', borderRadius: '8px', mb: 2 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#166534' }}>Gross Profit</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#166534' }}>{formatCurrency(incomeStatement.gross_profit)}</Typography>
                                    </Box>
                                )}
                                {renderReportSection('Operating Expenses', incomeStatement.expenses || incomeStatement.operating_expenses)}
                                {incomeStatement.net_income !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#f0fdf4', borderRadius: '8px', border: '2px solid #22c55e', mt: 2 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: '#166534' }}>Net Income</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: incomeStatement.net_income >= 0 ? '#166534' : '#991b1b' }}>
                                            {formatCurrency(incomeStatement.net_income)}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Card>
                )}

                {/* Balance Sheet Tab */}
                {tabValue === 2 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                        {bsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
                        ) : !balanceSheet ? (
                            <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 4 }}>No data available</Typography>
                        ) : (
                            <>
                                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>Balance Sheet</Typography>
                                {renderReportSection('Assets', balanceSheet.assets)}
                                {balanceSheet.total_assets !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#eff6ff', borderRadius: '8px', mb: 2, border: '1px solid #bfdbfe' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#1e40af' }}>Total Assets</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#1e40af' }}>{formatCurrency(balanceSheet.total_assets)}</Typography>
                                    </Box>
                                )}
                                {renderReportSection('Liabilities', balanceSheet.liabilities)}
                                {balanceSheet.total_liabilities !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#fef3c7', borderRadius: '8px', mb: 2, border: '1px solid #fde68a' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#92400e' }}>Total Liabilities</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#92400e' }}>{formatCurrency(balanceSheet.total_liabilities)}</Typography>
                                    </Box>
                                )}
                                {renderReportSection('Equity', balanceSheet.equity)}
                                {balanceSheet.total_equity !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#f0fdf4', borderRadius: '8px', mb: 2, border: '1px solid #bbf7d0' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#166534' }}>Total Equity</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#166534' }}>{formatCurrency(balanceSheet.total_equity)}</Typography>
                                    </Box>
                                )}
                                {(balanceSheet.total_liabilities !== undefined && balanceSheet.total_equity !== undefined) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '2px solid #e2e8f0', mt: 2 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>Liabilities + Equity</Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>
                                            {formatCurrency((balanceSheet.total_liabilities || 0) + (balanceSheet.total_equity || 0))}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Card>
                )}
            </Box>
        </Box>
    );
}
